import { t } from 'i18next';
import { ArrowUp, Check, LoaderCircle, Star } from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';

import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Textarea } from '@/components/ui/textarea';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { toast } from '@/components/ui/use-toast';
import { AI_BUILDER_SIGNALS } from '@activepieces/shared';

import { useBuilderStateContext } from '../builder-hooks';
import { useFlowPublish, useTestFlowRun } from '../use-flow-actions';

import { AddReviewDialog } from './add-review-dialog';
import { localiseCardSubject, toCardSelectChoices } from './ai-builder-utils';
import { CardChoicePicker } from './card-choice-picker';
import { ChatMessage } from './chat-message';
import { LoadingMessage } from './loading-message';
import { useAiBuilderTurn } from './use-ai-builder-turn';
import { useChatStore } from './useChatStore';

// The first message sits flush; a bot follow-up hugs the bubble above it
const messageSpacingClass = (
  index: number,
  isConsecutiveBot: boolean,
): string => {
  if (index === 0) return '';
  return isConsecutiveBot ? 'mt-3' : 'mt-6';
};

// A wizard pair shares the row; a lone primary action while the flow is still
// being built takes the full width
const suggestedActionClassName = ({
  isWizardActionPair,
  isFirst,
}: {
  isWizardActionPair: boolean;
  isFirst: boolean;
}): string => {
  if (isWizardActionPair) return isFirst ? 'flex-[2]' : 'flex-1';
  return isFirst ? 'w-full' : '';
};

const ChatInput = ({
  inputMessage,
  setInputMessage,
  handleSendMessage,
  isPending,
  textAreaRef,
}: {
  inputMessage: string;
  setInputMessage: (value: string) => void;
  handleSendMessage: () => void;
  isPending: boolean;
  textAreaRef: React.RefObject<HTMLTextAreaElement>;
}) => (
  <div className="flex items-center mt-2 mb-4 mx-2 p-1 gap-2 bg-background border border-solid border-outline shadow-md rounded-md">
    <Textarea
      ref={textAreaRef}
      value={inputMessage}
      className="w-full p-2 border-none resize-none focus-visible:ring-0 focus-visible:ring-offset-0"
      minRows={1}
      autoFocus={true}
      maxRows={4}
      placeholder={t('i.e I want to create a flow that ...')}
      onChange={(e) => setInputMessage(e.target.value)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' && !e.shiftKey && !isPending) {
          e.preventDefault();
          handleSendMessage();
        }
      }}
    />
    <Button
      size="xs"
      className="shrink-0 flex items-center h-8"
      onClick={handleSendMessage}
      disabled={isPending}
      aria-label={t('Send')}
    >
      {isPending ? (
        <LoaderCircle className="w-5 h-5 animate-spin" />
      ) : (
        <ArrowUp className="w-5 h-5" />
      )}
    </Button>
  </div>
);

export const GenerateFlow = () => {
  const flowVersion = useBuilderStateContext((state) => state.flowVersion);
  const flowId = flowVersion.flowId;
  const { chats, setMessages } = useChatStore();
  const messages = chats[flowId] || [];

  // The turn protocol (sending, resume token, retry, socket events) lives in
  // the hook; this component keeps rendering and DOM concerns only
  const {
    sendMessage,
    sendSilentAction,
    retryTurn,
    isGenerating,
    isAnswerLost,
    loadingMessage,
  } = useAiBuilderTurn();

  // Extract suggested actions from the last bot message for the sticky bar
  const lastBotMessage = [...messages]
    .reverse()
    .find((m) => m.userType === 'bot');
  const lastBotSuggestedActions = lastBotMessage?.suggestedActions ?? [];
  const lastBotSelectChoices = localiseCardSubject(
    toCardSelectChoices(lastBotMessage?.card),
    lastBotMessage?.contentParams?.pieceName,
    (key, ns) => t(key, { ns }),
  );
  const stickyActions = lastBotSuggestedActions.filter((action) =>
    action.message.startsWith('['),
  );
  const inlineAnswerActions = lastBotSelectChoices
    ? []
    : lastBotSuggestedActions.filter(
        (action) => !action.message.startsWith('['),
      );
  // The SERVER says when the wizard finished (same signal that wrote the
  // summary and flipped the steps valid). Inferring it from the presence of
  // improvement suggestions missed every completion that carries none — a
  // turn ending on a skipped test, for one.
  const isFlowReady = lastBotMessage?.flowReady === true;
  // Wizard mode shows exactly two actions (confirm + help) side by side
  const isWizardActionPair = stickyActions.length === 2;
  const [openReviewDialog, setOpenReviewDialog] = useState(false);
  const [inputMessage, setInputMessage] = useState('');
  const lastMessageRef = useRef<HTMLDivElement>(null);
  const textAreaRef = useRef<HTMLTextAreaElement>(null);
  const { publish, isPublishing, canPublish } = useFlowPublish();
  const { testFlow, isTesting, canTestFlow } = useTestFlowRun();

  const handleSendMessage = async () => {
    const trimmedInputMessage = inputMessage.trim();
    if (trimmedInputMessage.length === 0) return;
    setInputMessage('');
    void sendMessage(trimmedInputMessage);
  };

  useEffect(() => {
    if (textAreaRef.current) {
      textAreaRef.current.focus();
    }
  }, []);

  // One place instead of five imperative call sites: every new message (user,
  // bot, narration) and the loader's appearance scroll the chat to its tail
  useEffect(() => {
    const timeout = setTimeout(() => {
      lastMessageRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, 1);
    return () => clearTimeout(timeout);
  }, [messages.length, isGenerating]);

  return (
    <div className="flex flex-col h-full">
      <div className="flex flex-col flex-grow overflow-hidden">
        <ScrollArea className="flex-grow overflow-auto">
          <div className="flex flex-col p-2">
            {messages.map((message, index) => {
              const prevMessage = index > 0 ? messages[index - 1] : null;
              const isConsecutiveBot =
                message.userType === 'bot' && prevMessage?.userType === 'bot';
              return (
                <div
                  key={index}
                  className={messageSpacingClass(index, isConsecutiveBot)}
                >
                  {isConsecutiveBot && (
                    <div className="ml-10 mx-2 mb-3 border-t border-gray-100 dark:border-gray-800" />
                  )}
                  <ChatMessage
                    message={message}
                    ref={index === messages.length - 1 ? lastMessageRef : null}
                    isLastMessage={
                      index === messages.length - 1 &&
                      message.userType === 'bot'
                    }
                    onUndo={() => setMessages(flowId, messages.slice(0, -1))}
                    onRetry={() => retryTurn(message)}
                    showAvatar={!isConsecutiveBot}
                  >
                    {message.answeredChoice && (
                      // The pick stays where the question was asked
                      <div className="mt-2 flex items-center gap-1.5 text-sm text-muted-foreground">
                        <Check className="w-3.5 h-3.5 text-primary" />
                        <span>{message.answeredChoice}</span>
                      </div>
                    )}
                    {index === messages.length - 1 &&
                      message.userType === 'bot' &&
                      !message.answeredChoice &&
                      !isGenerating &&
                      inlineAnswerActions.length > 0 && (
                        // Free-text answers sit right under the question they
                        // answer — in the bottom bar they read detached
                        <div className="mt-2 flex flex-wrap gap-2">
                          {inlineAnswerActions.map((action) => (
                            <Button
                              key={action.message}
                              size="sm"
                              variant="outline"
                              className="h-auto min-h-8 max-w-full whitespace-normal text-left"
                              // Translated so free-text button messages reach
                              // the model in the user's language; the label
                              // stays under the question as the answer
                              onClick={() =>
                                sendSilentAction(
                                  t(action.message),
                                  t(action.label),
                                )
                              }
                            >
                              {t(action.label)}
                            </Button>
                          ))}
                        </div>
                      )}
                    {index === messages.length - 1 &&
                      message.userType === 'bot' &&
                      !message.answeredChoice &&
                      lastBotSelectChoices &&
                      !isGenerating && (
                        // The select sits right under the message it answers —
                        // a value list floating in the bottom bar reads detached
                        <div className="mt-2">
                          <CardChoicePicker
                            choices={lastBotSelectChoices}
                            onPick={(option) =>
                              sendSilentAction(
                                `${
                                  AI_BUILDER_SIGNALS.CARD_REPLY
                                } ${JSON.stringify({
                                  cardId: lastBotSelectChoices.cardId,
                                  value: option.value,
                                })}`,
                                option.label,
                              )
                            }
                          />
                        </div>
                      )}
                  </ChatMessage>
                </div>
              );
            })}
            {isGenerating && (
              <div className="mt-6">
                <LoadingMessage message={loadingMessage} />
              </div>
            )}
          </div>
        </ScrollArea>

        {isAnswerLost && (
          <div className="px-3 py-2">
            <Button
              className="w-full"
              onClick={() => sendSilentAction(AI_BUILDER_SIGNALS.RESUME)}
            >
              {t('Resume the conversation')}
            </Button>
          </div>
        )}
        {/* The previous answer's actions are stale until the lost one is
            resumed: they answer a question the user already moved past */}
        {isFlowReady && !isGenerating && !isAnswerLost && (
          <div className="px-3 pt-2 flex flex-col gap-2">
            <div className="flex gap-2">
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    size="sm"
                    variant="outline"
                    className="flex-1"
                    disabled={!canTestFlow || isTesting}
                    onClick={() => testFlow()}
                  >
                    {t('Test the flow')}
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  {t(
                    'The actions really run: emails are sent, rows are added.',
                  )}
                </TooltipContent>
              </Tooltip>
              <Button
                size="sm"
                className="flex-1"
                disabled={!canPublish || isPublishing}
                onClick={publish}
              >
                {t('Publish the flow')}
              </Button>
            </div>
            <button
              onClick={() => setOpenReviewDialog(true)}
              className="flex items-center justify-center gap-1 w-full text-xs text-muted-foreground hover:text-foreground transition-colors"
            >
              <Star className="w-3 h-3" />
              {t('Rate this experience')}
            </button>
          </div>
        )}
        {stickyActions.length > 0 &&
          // Select-able cards render inline under their message; the sticky
          // quick-replies would duplicate the same options as buttons
          !lastBotSelectChoices &&
          !lastBotMessage?.answeredChoice &&
          !isGenerating &&
          !isAnswerLost && (
            <div className="px-3 py-2">
              <div
                className={
                  isWizardActionPair ? 'flex gap-2' : 'flex flex-wrap gap-2'
                }
              >
                {stickyActions.map((action, idx) => (
                  <Button
                    key={action.message}
                    size={isWizardActionPair || idx === 0 ? 'default' : 'sm'}
                    variant={idx === 0 ? 'default' : 'outline'}
                    className={suggestedActionClassName({
                      isWizardActionPair,
                      isFirst: idx === 0,
                    })}
                    // Protocol signals ([STEP_DONE]…) match no i18n key and
                    // pass through t() unchanged
                    // No trace kept: these are ACTIONS (run the test, skip,
                    // done), and the next message shows what they produced.
                    // Only ANSWERS — a picked option, a framing reply — are
                    // worth keeping under the question they settle.
                    onClick={() => sendSilentAction(t(action.message))}
                  >
                    {t(action.label)}
                  </Button>
                ))}
              </div>
            </div>
          )}

        <ChatInput
          inputMessage={inputMessage}
          setInputMessage={setInputMessage}
          handleSendMessage={handleSendMessage}
          isPending={isGenerating}
          textAreaRef={textAreaRef}
        />
      </div>

      <AddReviewDialog
        open={openReviewDialog}
        onClose={() => setOpenReviewDialog(false)}
        onSubmit={() => {
          toast({
            variant: 'success',
            title: t('Review submitted'),
          });
          setOpenReviewDialog(false);
        }}
      />
    </div>
  );
};

GenerateFlow.displayName = 'GenerateFlowSidebar';
